import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { TICKET_STATUSES } from '../lib/constants';
import { parseServerDate } from '../lib/constants';

const MONTH_LABELS = [
  'Januari','Februari','Maret','April','Mei','Juni',
  'Juli','Agustus','September','Oktober','November','Desember'
];
const DOW = ['Min','Sen','Sel','Rab','Kam','Jum','Sab'];

const toLocalDate = (str) => {
  if (!str) return null;
  try { return new Date(str); } catch (_) { return null; }
};

const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const SchedulePage = () => {
  const [tickets, setTickets] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());

  const rangeStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const rangeEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59);

  useEffect(() => {
    const startISO = rangeStart.toISOString().slice(0, 10);
    const endISO = new Date(rangeEnd.getTime() + 86400000).toISOString().slice(0, 10);
    api.get(`/tickets/schedule/calendar?start=${startISO}&end=${endISO}`)
      .then((r) => setTickets(r.data || []))
      .catch(() => setTickets([]));
  }, [cursor]);

  const cells = useMemo(() => {
    const firstDow = rangeStart.getDay();
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < firstDow; i++) arr.push(null);
    for (let d = 1; d <= days; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const ticketsByDay = useMemo(() => {
    const map = {};
    for (const t of tickets) {
      const d = toLocalDate(t.scheduled_start);
      if (!d) continue;
      const key = d.toDateString();
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    return map;
  }, [tickets]);

  // Count schedules per day, restricted to maintenance-type categories (umum, me) + IT
  const LOAD_CATEGORIES = new Set(['umum', 'me', 'it']);
  const loadByDay = useMemo(() => {
    const map = {};
    for (const t of tickets) {
      if (!LOAD_CATEGORIES.has(t.category)) continue;
      const d = toLocalDate(t.scheduled_start);
      if (!d) continue;
      const key = d.toDateString();
      map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [tickets]);

  const dayLoadClass = (count) => {
    if (count >= 4) return 'bg-red-50 border-red-300';
    if (count >= 2) return 'bg-amber-50 border-amber-300';
    return '';
  };

  const dayTickets = ticketsByDay[selected?.toDateString()] || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="schedule-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Scheduling</p>
            <h1 className="text-4xl font-medium tracking-tight">Jadwal Pengerjaan</h1>
            <p className="text-muted-foreground mt-1">Kalender tiket yang sudah dijadwalkan</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-[180px] text-center font-medium">
              {MONTH_LABELS[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setCursor(new Date()); setSelected(new Date()); }} data-testid="today-button">
              Hari Ini
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar grid */}
          <Card className="border-border lg:col-span-2">
            <CardContent className="p-4">
              <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                {DOW.map((d) => (
                  <div key={d} className="bg-secondary/60 text-xs font-medium py-2 text-center">{d}</div>
                ))}
                {cells.map((date, i) => {
                  const isToday = date && sameDay(date, new Date());
                  const isSelected = date && sameDay(date, selected);
                  const dayList = date ? (ticketsByDay[date.toDateString()] || []) : [];
                  const load = date ? (loadByDay[date.toDateString()] || 0) : 0;
                  const loadClass = dayLoadClass(load);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => date && setSelected(date)}
                      className={`min-h-[92px] p-2 text-left transition-colors relative border ${
                        !date ? 'opacity-30 pointer-events-none bg-background border-transparent' :
                        loadClass ? `${loadClass} hover:opacity-90` : 'bg-background border-transparent hover:bg-secondary/40'
                      } ${isSelected ? 'ring-2 ring-primary ring-inset' : ''}`}
                      data-testid={date ? `calendar-day-${date.toISOString().slice(0,10)}` : undefined}
                    >
                      {date && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-medium ${isToday ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground' : ''}`}>
                              {date.getDate()}
                            </span>
                            {load >= 2 && (
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${load >= 4 ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}
                                title={`${load} jadwal maintenance/IT hari ini`}
                                data-testid={`load-badge-${date.toISOString().slice(0,10)}`}>
                                {load}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 space-y-0.5">
                            {dayList.slice(0, 2).map((t) => (
                              <div key={t.id} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary truncate">
                                {t.title}
                              </div>
                            ))}
                            {dayList.length > 2 && (
                              <div className="text-[10px] text-muted-foreground">+{dayList.length - 2} lagi</div>
                            )}
                          </div>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-300"></span>2-3 jadwal (maintenance/IT)</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-300"></span>4+ jadwal (padat)</span>
              </div>
            </CardContent>
          </Card>

          {/* Day detail panel */}
          <Card className="border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg tracking-tight font-medium flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                {selected ? selected.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Pilih Tanggal'}
              </CardTitle>
              <p className="text-xs text-muted-foreground">{dayTickets.length} tiket dijadwalkan</p>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              {dayTickets.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Tidak ada jadwal</p>
                </div>
              )}
              {dayTickets.map((t) => (
                <Link
                  key={t.id}
                  to={`/tickets/${t.ticket_number}`}
                  className="block p-3 rounded-md border border-border hover:bg-secondary/40 transition-colors"
                  data-testid={`schedule-ticket-${t.ticket_number}`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10px] bg-secondary px-1.5 py-0.5 rounded">{t.ticket_number}</span>
                    <Badge className={`text-[10px] ${TICKET_STATUSES[t.status]?.color || ''}`}>
                      {TICKET_STATUSES[t.status]?.label || t.status}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium">{t.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t.scheduled_start && parseServerDate(t.scheduled_start).toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' })}
                    {t.scheduled_end && t.scheduled_end !== t.scheduled_start && ` — ${parseServerDate(t.scheduled_end).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' })}`}
                    {t.location_id && ` • ${t.location_id}`}
                  </p>
                  {t.schedule_note && <p className="text-xs text-muted-foreground italic mt-1">{t.schedule_note}</p>}
                </Link>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SchedulePage;
